import styles from 'css/src/components/pricing/comparison-table.module.scss';

import React from 'react';
import classNames from 'classnames';

import type { Plan, PlanFeatures } from 'js/src/client';
import { BillingCycleEnum } from 'js/src/client';
import Table, { TableBorder } from 'js/src/components/common/ui/Table';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import UpgradeButton from 'js/src/components/pricing/UpgradeButton';
import { pluralize } from 'js/src/libs/custom-utils';

const COLUMN_CLASSES: Record<number, string> = {
    0: styles['column-0'],
    1: styles['column-1'],
    2: styles['column-2'],
    3: styles['column-3'],
};

const isBusinessPlan = (planName: string) =>
    planName === 'Business_v3_Annual' ||
    planName === 'Business_v3' ||
    planName === 'Business_v3_Annual_Promotion' ||
    planName === 'Business_Post_Appsumo_Annual';

const getPlanDisplayName = (plan: Plan) => {
    return plan.display_name;
};

const generateFeatureCell = (title: string, tooltip?: string) => {
    return (
        <td>
            <Tooltip title={tooltip} show={!!tooltip}>
                <span
                    className={classNames({
                        [styles['dotted-underline']]: !!tooltip,
                    })}
                >
                    {title}
                </span>
            </Tooltip>
        </td>
    );
};

type Props = {
    plans: Plan[];
    switchToPlan: (plan: Plan) => void;
    handlePlanSwitchError: (error_info: PlanSwitchErrorInfo) => void;
    isAnnualPromo?: boolean;
};

const ComparisonTable = React.memo(function ComparisonTable({
    plans,
    switchToPlan,
    handlePlanSwitchError,
    isAnnualPromo,
}: Props) {
    const generateHeaderRowData = React.useCallback(
        (header: React.ReactNode) => {
            const nodes = [
                <td key="header">{header}</td>,
                ...plans.map((plan, index) => {
                    const name = getPlanDisplayName(plan);
                    return (
                        <td
                            key={name}
                            className={classNames(
                                COLUMN_CLASSES[index],
                                styles['color-inherit'],
                                'text-center',
                            )}
                        >
                            {name}
                        </td>
                    );
                }),
            ];
            return <tr className="fw-semibold">{nodes}</tr>;
        },
        [plans],
    );

    const generateCustomRowData = React.useCallback(
        (plansParam: Plan[], func: (plan: Plan) => React.ReactNode) => {
            return plansParam.map((plan, index) => {
                let val: React.ReactNode = func(plan);
                if (val === true) {
                    val = (
                        <i
                            className={classNames(
                                styles['color-inherit'],
                                'material-symbols-outlined icon-l5 icon-l5--xlarge',
                            )}
                        >
                            check
                        </i>
                    );
                } else if (val === false || val === 0) {
                    val = <span className="text-muted">&#8212;</span>;
                }
                return (
                    <td
                        key={plan.name}
                        className={classNames(COLUMN_CLASSES[index], 'text-center')}
                    >
                        {val}
                    </td>
                );
            });
        },
        [],
    );

    const generateSimpleRowData = React.useCallback(
        (plansParam: Plan[], key: keyof PlanFeatures) => {
            return generateCustomRowData(plansParam, (plan: Plan) => plan.features[key]);
        },
        [generateCustomRowData],
    );

    if (plans === undefined) {
        return null;
    }

    return (
        <Table className={styles['comparison-table']} border={TableBorder.None} responsive={true}>
            <thead>
                <tr>
                    <th />
                    {plans.map((plan, index) => {
                        let price_label: string | null;
                        if (plan.is_enterprise) {
                            price_label = "Let's talk!";
                        } else {
                            const frequency = 'month';
                            const price = plan.price;
                            let displayPrice = `${price}`;
                            // For the annual promo page, we display the price per month even though
                            // the plans are annual
                            if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                                const monthlyPrice = price / 12.0;
                                displayPrice =
                                    monthlyPrice % 1 !== 0
                                        ? monthlyPrice.toFixed(2)
                                        : `${monthlyPrice}`;
                            }
                            price_label = `$${displayPrice} USD/${frequency}`;
                        }
                        return (
                            <th
                                key={plan.name}
                                className={classNames(
                                    COLUMN_CLASSES[index],
                                    'text-center pt-l5-24 pb-l5-48',
                                )}
                            >
                                <div
                                    className={classNames(
                                        styles['color-inherit'],
                                        'font-l5 font-l5--h5 fw-semibold',
                                    )}
                                >
                                    {getPlanDisplayName(plan)}
                                </div>
                                <div className="font-l5 font-l5--regular">
                                    {price_label}
                                    {(isAnnualPromo ||
                                        plan.billing_cycle === BillingCycleEnum.ANNUAL) &&
                                        !plan.is_enterprise && (
                                            <>
                                                <br />
                                                <span className="font-l5 font-l5--subtext">
                                                    billed yearly
                                                </span>
                                            </>
                                        )}
                                </div>
                                <div className="mt-l5-16">
                                    <UpgradeButton
                                        key={plan.name}
                                        plan={plan}
                                        switchToPlan={switchToPlan}
                                        handlePlanSwitchError={handlePlanSwitchError}
                                        className="btn btn-extra-small"
                                    />
                                </div>
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {generateHeaderRowData('Usage')}
                <tr>
                    {generateFeatureCell(
                        'Team members',
                        'Collaborate with your team all in one place',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Multiple users';
                        } else if (
                            plan.features.purchased_seat_ceiling > 1 ||
                            plan.features.default_team_seats > 1
                        ) {
                            return 'Multiple users';
                        } else {
                            return 'Single user';
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Video limits',
                        'No cap on how many videos you can create and publish',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        const features = plan.features;
                        const numVideosPerMonth = features.num_videos_per_month;
                        if (!numVideosPerMonth) {
                            return 'Unlimited';
                        }
                        if (isAnnualPromo) {
                            return numVideosPerMonth * 12;
                        }
                        return numVideosPerMonth;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Maximum video duration',
                        'Create videos that are long enough to tell your story',
                    )}
                    {generateCustomRowData(plans, () => {
                        return '10 minutes';
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Animation minutes',
                        'Animation minutes are used when you animate scenes. Need more? You can add extra minutes as an add-on.',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }

                        // The plan value is already the per-cycle total (annual plans carry the
                        // full-year amount), so it is shown as-is without an annual multiplier.
                        const minutesPerCycle = plan.features.plan_animation_minutes_per_cycle;

                        if (!minutesPerCycle || minutesPerCycle === 0) {
                            return false;
                        }

                        const isAnnual =
                            isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL;
                        const timeUnit = pluralize(minutesPerCycle, 'min');
                        const unit = isAnnual ? '/year' : '/month';

                        return `${minutesPerCycle.toLocaleString()} ${timeUnit}${unit}`;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Published AI voiceover minutes',
                        "Editing, regenerating or swapping the voice before you publish doesn't use minutes",
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }

                        let value = plan.features.num_ai_vo_minutes_per_month ?? 0;
                        let unit = 'mins/month';

                        if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                            value = value * 12;
                            unit = 'mins/year';
                        }

                        return `${value} ${unit}`;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Blueprints',
                        'Create video outlines you can use over and over again',
                    )}
                    {generateCustomRowData(plans, () => {
                        return 'Unlimited';
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Workspaces',
                        'Multiple dashboards for different teams or use cases',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        } else {
                            return plan.features.max_workspaces;
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Brand kits',
                        'Apply your brand’s colours, fonts, and watermark with one click',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }
                        // Basic plan has 1 brand kit, but it cannot actually be applied.
                        // So we show 0 here to avoid confusion.
                        if (plan.name.startsWith('Basic')) {
                            return <span className="text-muted">&#8212;</span>;
                        } else {
                            return plan.features.max_brand_kits;
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Translations',
                        'Automatically localize your video’s text or captions',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.features.max_translations_per_month === null) {
                            return 'Unlimited';
                        }

                        let value = plan.features.max_translations_per_month;
                        let unit = 'month';

                        if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                            value = value * 12;
                            unit = 'year';
                        }

                        return `${value}/${unit}`;
                    })}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Rendering')}
                <tr>
                    {generateFeatureCell('Resolution')}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.features.render_1080p) {
                            return '1080p';
                        } else if (plan.features.render_720p) {
                            return '720p';
                        } else {
                            return '480p';
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Multiple formats',
                        'Easily swap between landscape, portrait, square, or vertical aspect ratios',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Branding')}
                <tr>
                    {generateFeatureCell(
                        'Branded outro',
                        'Show your brand’s logo and call-to-action, or upload your own outro',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        '30+ popular templates',
                        'Professionally designed scenes and animations for every use case',
                    )}
                    {generateSimpleRowData(plans, 'preset_themes')}
                </tr>
                {/* <tr>
                        {generateFeatureCell(
                            'Custom outro',
                            'Upload your own image or animation as the ending sequence of your videos'
                        )}
                        {generateCustomRowData(plans, () => true)}
                    </tr> */}
                <tr>
                    {generateFeatureCell(
                        'Custom colors',
                        'Make your videos pixel-perfect with your brand’s colors',
                    )}
                    {generateSimpleRowData(plans, 'custom_themes')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Choose from preset fonts',
                        'Customize your videos with 35+ fonts to choose from',
                    )}
                    {generateSimpleRowData(plans, 'custom_themes')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Upload your own fonts',
                        'Use your brand’s specific fonts',
                    )}
                    {generateSimpleRowData(plans, 'custom_font')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Watermark upload',
                        'Display your brand’s watermark in the corner of your video',
                    )}
                    {generateSimpleRowData(plans, 'custom_watermark')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Custom on-brand templates',
                        'Bespoke templates created for you by our in-house designers',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Custom transitions',
                        'Bespoke templates created for you by our in-house designers',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Bespoke scene designs',
                        'Bespoke templates created for you by our in-house designers',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Lumen5 Design Team Access',
                        'Bespoke templates created for you by our in-house designers',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Media')}
                <tr>
                    {generateFeatureCell(
                        'Millions of worry-free licensed images and videos',
                        'Lumen5’s media library is fully licensed for use',
                    )}
                    {generateSimpleRowData(plans, 'cc_media')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Upload your own videos & photos',
                        'Upload and tag your own media library',
                    )}
                    {generateSimpleRowData(plans, 'upload_media')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Upload your own audio tracks',
                        'Use your own music on your videos',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Unlimited premium media',
                        '50M+ additional images and videos from Shutterstock',
                    )}
                    {generateSimpleRowData(plans, 'shutterstock_unlimited_media')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Exclusive marketplace and editorial media',
                        'Use monthly credits towards Shutterstock’s top quality stock and editorial media',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }
                        // for free/Starter, don't show credit listing
                        else if (plan.features.asset_credits <= 5) {
                            return false;
                        } else {
                            let unit = 'month';
                            if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                                unit = 'year';
                            }

                            return `${plan.features.public_asset_credits}/${unit}`;
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Premium AI voice library',
                        'Choose from hundreds of realistic AI voices',
                    )}
                    {generateSimpleRowData(plans, 'voiceover')}
                </tr>
                <tr>
                    {generateFeatureCell('Icons', "Access to Lumen5's exclusive icon library")}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Automated captions',
                        'Automatically generate captions for talking head videos',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Talking head overlay designs',
                        'Making talking head videos stand out by adding overlays like callouts and cutaways',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'DAM Integration',
                        'Access media from your digital asset management system through a custom integration',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Voice cloning',
                        'Clone your own voice to use in AI voiceovers',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }
                        const maxClones = plan.features.max_voice_clones;
                        if (!maxClones) {
                            return false;
                        }
                        return `${maxClones} ${pluralize(maxClones, 'voice')}`;
                    })}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Team management')}
                <tr>
                    {generateFeatureCell(
                        'Two-factor authentication',
                        'Secure your user accounts with peace of mind',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Brand management',
                        'Create and manage brand-compliant templates',
                    )}
                    {generateCustomRowData(
                        plans,
                        (plan) => plan.is_enterprise || isBusinessPlan(plan.name),
                    )}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Granular user permissions',
                        'Flexible roles and permissions for all sizes of teams',
                    )}
                    {generateCustomRowData(
                        plans,
                        (plan) => plan.is_enterprise || isBusinessPlan(plan.name),
                    )}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Workspace management',
                        'Keep your account clutter-free with multiple workspaces',
                    )}
                    {generateCustomRowData(
                        plans,
                        (plan) => plan.is_enterprise || isBusinessPlan(plan.name),
                    )}
                </tr>
                <tr>
                    {generateFeatureCell('Single sign-on', 'Streamline user management securely')}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell('Video approval workflow')}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Audit log',
                        'Track and review all account activity for compliance and security',
                    )}
                    {generateSimpleRowData(plans, 'has_audit_log')}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Support')}
                <tr>
                    {generateFeatureCell(
                        'Help center access',
                        'Answers for all your questions in our help center',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Chat support',
                        'Additional support available via live chat',
                    )}
                    {generateSimpleRowData(plans, 'chat_support')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Customized support',
                        'Dedicated Customer Success Manager for your whole team',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell('Invoiced billing')}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell('Procurement Support')}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell('Security Documentation & Support')}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell('Training Workshops')}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
            </tbody>
        </Table>
    );
});

export default ComparisonTable;
